import React, { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { endpoints } from "@/api";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ClipboardCheck, Plus, Trash2, Printer, X, FileText, Check, AlertTriangle, XCircle, Minus, ChevronDown, ChevronUp, Eye, Pencil, Search, Filter, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "./ConfirmDialog";
import { RowSkeleton } from "./Skeletons";

const STATUS_BUTTONS = [
  { val: "pass", icon: Check,         color: "bg-emerald-600 text-white border-emerald-600", border: "border-emerald-200 text-emerald-700 hover:bg-emerald-50" },
  { val: "warn", icon: AlertTriangle, color: "bg-amber-500 text-white border-amber-500",    border: "border-amber-200 text-amber-700 hover:bg-amber-50" },
  { val: "fail", icon: XCircle,       color: "bg-red-600 text-white border-red-600",        border: "border-red-200 text-red-700 hover:bg-red-50" },
  { val: "na",   icon: Minus,         color: "bg-gray-400 text-white border-gray-400",      border: "border-gray-200 text-gray-500 hover:bg-gray-50" },
];

const STATUS_LABEL_AR = { pass: "ممتاز", warn: "تحت المتوسط", fail: "يحتاج تحسين", na: "غير منطبق" };
const STATUS_LABEL_EN = { pass: "Pass", warn: "Warn", fail: "Fail", na: "N/A" };
const GROUP_LABEL = {
  coffee:      { ar: "علم القهوة",  en: "Coffee science",  emoji: "☕" },
  cleanliness: { ar: "النظافة",     en: "Cleanliness",     emoji: "🧼" },
  operations:  { ar: "العمليات",    en: "Operations",      emoji: "⚙️" },
  staff:       { ar: "الموظفون",    en: "Staff",           emoji: "👥" },
  atmosphere:  { ar: "الأجواء",     en: "Atmosphere",      emoji: "🎵" },
};

export default function VisitsTab() {
  const { isRTL, lang } = useI18n();
  const qc = useQueryClient();
  const { confirm, dialog } = useConfirm();
  const [creating, setCreating] = useState(false);
  const [filterBranch, setFilterBranch] = useState("all");
  const [viewVisit, setViewVisit] = useState(null); // visit being viewed
  const [editVisit, setEditVisit] = useState(null); // visit being edited

  const { data: visits = [], isLoading } = useQuery({
    queryKey: ["mycafe-visits"], queryFn: () => endpoints.myCafeVisits(),
  });
  const { data: branches = [] } = useQuery({
    queryKey: ["mycafe-branches"], queryFn: endpoints.myCafeBranches,
  });
  const { data: checklist } = useQuery({
    queryKey: ["mycafe-checklist"], queryFn: endpoints.myCafeChecklist,
  });

  const filtered = useMemo(
    () => filterBranch === "all" ? visits : visits.filter((v) => v.branch_id === filterBranch),
    [visits, filterBranch]
  );

  const handleDelete = async (v) => {
    const ok = await confirm({
      title: isRTL ? "حذف الزيارة؟" : "Delete visit?",
      description: isRTL ? "لا يمكن التراجع بعد الحذف." : "This action cannot be undone.",
      destructive: true,
      confirmLabel: isRTL ? "نعم، احذف" : "Yes, delete",
    });
    if (!ok) return;
    await endpoints.myCafeDeleteVisit(v.id);
    qc.invalidateQueries({ queryKey: ["mycafe-visits"] });
    qc.invalidateQueries({ queryKey: ["mycafe-overview"] });
    toast.success(isRTL ? "تم الحذف" : "Deleted");
  };

  return (
    <div className="space-y-6" data-testid="mycafe-visits">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <p className="text-sm text-[#5C544F] max-w-2xl">
          {isRTL
            ? "كل زيارة تنشئ تقريراً موثقاً بالتاريخ والوقت ودرجة جودة من ١٠٠، ويمكن طباعته PDF وتسليمه لصاحب البزنس."
            : "Each visit creates a date-stamped report with a 0–100 quality score and a printable PDF for the business owner."}
        </p>
        {!creating && (
          <Button onClick={() => setCreating(true)} className="bg-[#2A2421] text-[#F9F6F0]" data-testid="visit-add-btn" disabled={branches.length === 0}>
            <Plus className="w-4 h-4 me-1.5" />
            {isRTL ? "زيارة جديدة" : "New visit"}
          </Button>
        )}
      </div>

      {branches.length === 0 && (
        <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900">
          {isRTL ? "أضف فرعاً من تبويب الفروع قبل إنشاء أي زيارة." : "Add a branch first from the Branches tab before creating a visit."}
        </div>
      )}

      {creating && (
        <VisitForm
          checklist={checklist?.items || []}
          branches={branches}
          onClose={() => setCreating(false)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ["mycafe-visits"] });
            qc.invalidateQueries({ queryKey: ["mycafe-overview"] });
            setCreating(false);
          }}
        />
      )}

      {/* Filter bar */}
      {visits.length > 0 && !creating && (
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-xs text-[#5C544F] inline-flex items-center gap-1.5">
            <Filter className="w-3 h-3" /> {isRTL ? "فلتر:" : "Filter:"}
          </div>
          <Select value={filterBranch} onValueChange={setFilterBranch}>
            <SelectTrigger className="h-9 w-56" data-testid="visit-filter-branch"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{isRTL ? "كل الفروع" : "All branches"}</SelectItem>
              {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="text-xs text-[#5C544F]">
            {filtered.length} {isRTL ? "زيارة" : "visits"}
          </div>
        </div>
      )}

      {isLoading ? (
        <RowSkeleton rows={3} />
      ) : filtered.length === 0 && !creating ? (
        <EmptyState
          icon={ClipboardCheck}
          title={isRTL ? "لا توجد زيارات بعد" : "No visits yet"}
          subtitle={isRTL ? "ابدأ أول زيارة جودة لقياس أداء فرعك." : "Start your first quality visit to measure branch performance."}
          ctaLabel={isRTL ? "زيارة جديدة" : "New visit"}
          onCta={branches.length > 0 ? () => setCreating(true) : null}
        />
      ) : (
        <div className="space-y-3">
          {filtered.map((v) => (
            <VisitRow
              key={v.id}
              v={v}
              lang={lang}
              isRTL={isRTL}
              onView={() => setViewVisit(v)}
              onEdit={() => setEditVisit(v)}
              onDelete={() => handleDelete(v)}
            />
          ))}
        </div>
      )}

      {/* View modal */}
      <VisitDetailModal
        open={!!viewVisit}
        visit={viewVisit}
        checklist={checklist?.items || []}
        lang={lang}
        onClose={() => setViewVisit(null)}
        onEdit={(v) => { setViewVisit(null); setEditVisit(v); }}
      />

      {/* Edit modal */}
      <Dialog open={!!editVisit} onOpenChange={(o) => !o && setEditVisit(null)}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="visit-edit-modal">
          <DialogHeader>
            <DialogTitle>{isRTL ? "تعديل الزيارة" : "Edit visit"}</DialogTitle>
            <DialogDescription>
              {editVisit?.branch_name} · {editVisit?.visit_date ? new Date(editVisit.visit_date).toLocaleString(isRTL ? "ar-SA" : "en-GB") : ""}
            </DialogDescription>
          </DialogHeader>
          {editVisit && (
            <VisitForm
              embedded
              existing={editVisit}
              checklist={checklist?.items || []}
              branches={branches}
              onClose={() => setEditVisit(null)}
              onSaved={() => {
                qc.invalidateQueries({ queryKey: ["mycafe-visits"] });
                qc.invalidateQueries({ queryKey: ["mycafe-overview"] });
                setEditVisit(null);
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {dialog}
    </div>
  );
}

function VisitRow({ v, isRTL, lang, onView, onEdit, onDelete }) {
  const score = v.overall_score || 0;
  const color = score >= 80 ? "text-emerald-600" : score >= 60 ? "text-amber-500" : "text-red-500";
  const ring = score >= 80 ? "ring-emerald-200" : score >= 60 ? "ring-amber-200" : "ring-red-200";
  const dt = v.visit_date ? new Date(v.visit_date) : null;
  const pdfUrl = endpoints.myCafeVisitPdfUrl(v.id, lang);
  const counts = (v.items || []).reduce((acc, it) => ({ ...acc, [it.status]: (acc[it.status] || 0) + 1 }), {});
  return (
    <div className="soft-card rounded-2xl p-5 flex flex-wrap items-center justify-between gap-4 hover:shadow-md transition-shadow" data-testid={`visit-row-${v.id}`}>
      <button onClick={onView} className="flex items-center gap-4 flex-1 min-w-0 text-start">
        <div className={`text-3xl font-bold ${color} min-w-[64px] text-center ring-4 ${ring} rounded-2xl py-1.5 bg-white`} data-testid={`visit-score-${v.id}`}>{score}</div>
        <div className="min-w-0">
          <div className="text-[#2A2421] font-medium truncate">{v.branch_name || (isRTL ? "(بدون فرع)" : "(no branch)")}</div>
          <div className="text-xs text-[#5C544F]">
            {dt ? dt.toLocaleString(isRTL ? "ar-SA" : "en-GB") : ""}
            {v.barista_on_duty ? ` · ${v.barista_on_duty}` : ""}
          </div>
          <div className="text-xs text-[#5C544F] mt-1 flex gap-3">
            <span className="text-emerald-600">✓ {counts.pass || 0}</span>
            <span className="text-amber-600">⚠ {counts.warn || 0}</span>
            <span className="text-red-600">✗ {counts.fail || 0}</span>
          </div>
        </div>
      </button>
      <div className="flex gap-1 flex-shrink-0">
        <IconBtn onClick={onView} icon={Eye} label={isRTL ? "عرض" : "View"} testId={`visit-view-${v.id}`} />
        <IconBtn onClick={onEdit} icon={Pencil} label={isRTL ? "تعديل" : "Edit"} testId={`visit-edit-${v.id}`} />
        <a href={pdfUrl} target="_blank" rel="noreferrer"
           className="px-3 py-2 rounded-lg bg-[#C86240] text-white text-xs inline-flex items-center"
           data-testid={`visit-pdf-${v.id}`}>
          <Printer className="w-3.5 h-3.5 me-1.5" />
          {isRTL ? "PDF" : "PDF"}
        </a>
        <IconBtn onClick={onDelete} icon={Trash2} label="" testId={`visit-del-${v.id}`} danger />
      </div>
    </div>
  );
}

const IconBtn = ({ onClick, icon: Icon, label, testId, danger }) => (
  <button
    onClick={onClick}
    className={`p-2 rounded-lg ${danger ? "hover:bg-red-50" : "hover:bg-[#2A2421]/5"} inline-flex items-center gap-1.5 text-xs ${danger ? "text-red-500" : "text-[#5C544F]"}`}
    data-testid={testId}
    title={label}
  >
    <Icon className="w-3.5 h-3.5" />
    {label && <span className="hidden sm:inline">{label}</span>}
  </button>
);

const EmptyState = ({ icon: Icon, title, subtitle, ctaLabel, onCta }) => (
  <div className="rounded-2xl border border-dashed border-[#2A2421]/15 p-12 text-center bg-[#F9F6F0]/50" data-testid="empty-state">
    <div className="w-16 h-16 mx-auto rounded-full bg-[#C86240]/10 flex items-center justify-center mb-4">
      <Icon className="w-7 h-7 text-[#C86240]" />
    </div>
    <div className="text-[#2A2421] font-medium text-lg">{title}</div>
    {subtitle && <div className="text-sm text-[#5C544F] mt-1 max-w-md mx-auto">{subtitle}</div>}
    {ctaLabel && onCta && (
      <Button onClick={onCta} className="mt-5 bg-[#2A2421] text-[#F9F6F0]" data-testid="empty-state-cta">
        <Plus className="w-4 h-4 me-1.5" />
        {ctaLabel}
      </Button>
    )}
  </div>
);

// ============================================================
// Visit form (used both for new and edit)
// ============================================================
function VisitForm({ checklist, branches, existing = null, embedded = false, onClose, onSaved }) {
  const { isRTL } = useI18n();
  const isEdit = !!existing;
  const [branchId, setBranchId] = useState(existing?.branch_id || branches[0]?.id || "");
  const [inspectorName, setInspectorName] = useState(existing?.inspector_name || "");
  const [baristaOnDuty, setBaristaOnDuty] = useState(existing?.barista_on_duty || "");
  const [overallNotes, setOverallNotes] = useState(existing?.overall_notes || "");

  // Build merged items: default checklist + any custom items + hidden state
  const buildInitialItems = () => {
    if (existing?.items?.length) {
      // Existing visit — items array IS the source of truth (already filtered)
      return existing.items.map((it) => ({
        key: it.key,
        label: it.label || null,         // null = default checklist label
        group: it.group || null,
        status: it.status || "na",
        value: it.value || "",
        note: it.note || "",
        _hidden: false,
        _custom: !!it.label,             // any item with label is a custom one
      }));
    }
    return checklist.map((c) => ({
      key: c.key, label: null, group: null,
      status: "na", value: "", note: "",
      _hidden: false, _custom: false,
    }));
  };
  const [items, setItems] = useState(buildInitialItems);

  const [openGroups, setOpenGroups] = useState({ coffee: true });
  const [saving, setSaving] = useState(false);

  // Map of checklist meta by key (for default items)
  const catalog = useMemo(() => Object.fromEntries(checklist.map((c) => [c.key, c])), [checklist]);

  // Build grouped view from current items (respecting custom group)
  const grouped = useMemo(() => {
    const map = {};
    items.forEach((it) => {
      if (it._hidden) return;
      const meta = catalog[it.key];
      const groupKey = it.group || meta?.group || "other";
      (map[groupKey] = map[groupKey] || []).push({ it, meta });
    });
    return map;
  }, [items, catalog]);

  const setItem = (key, patch) =>
    setItems((arr) => arr.map((it) => (it.key === key ? { ...it, ...patch } : it)));

  const hideItem = (key) => setItem(key, { _hidden: true });
  const restoreItem = (key) => setItem(key, { _hidden: false });

  const addCustomItem = (groupKey, label) => {
    if (!label?.trim()) return;
    const key = `custom_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    setItems((arr) => [
      ...arr,
      { key, label: label.trim(), group: groupKey, status: "na", value: "", note: "", _hidden: false, _custom: true },
    ]);
    setOpenGroups((g) => ({ ...g, [groupKey]: true }));
  };

  const hiddenCount = items.filter((it) => it._hidden).length;

  // Running per-group progress (% of items already evaluated, excluding hidden)
  const groupProgress = (list) => {
    const total = list.length;
    const evaluated = list.filter(({ it }) => it.status !== "na").length;
    return { evaluated, total };
  };

  const livePreviewScore = useMemo(() => {
    const weight = { pass: 100, warn: 60, fail: 0 };
    const scored = items.filter((it) => !it._hidden && it.status in weight).map((it) => weight[it.status]);
    return scored.length ? Math.round(scored.reduce((a, b) => a + b, 0) / scored.length) : 0;
  }, [items]);

  const save = async () => {
    if (!branchId) return toast.error(isRTL ? "اختر الفرع" : "Choose a branch");
    setSaving(true);
    try {
      // Strip frontend-only flags and hidden items
      const cleanItems = items
        .filter((it) => !it._hidden)
        .map(({ _hidden, _custom, label, group, ...rest }) => ({
          ...rest,
          ...(label ? { label } : {}),
          ...(group ? { group } : {}),
        }));
      const payload = {
        branch_id: branchId,
        inspector_name: inspectorName || null,
        barista_on_duty: baristaOnDuty || null,
        overall_notes: overallNotes || null,
        items: cleanItems,
      };
      if (isEdit) await endpoints.myCafeUpdateVisit(existing.id, payload);
      else await endpoints.myCafeCreateVisit(payload);
      toast.success(isRTL ? "تم الحفظ" : "Saved");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  const previewColor = livePreviewScore >= 80 ? "text-emerald-600" : livePreviewScore >= 60 ? "text-amber-500" : "text-red-500";

  const Wrapper = embedded ? "div" : "div";

  return (
    <Wrapper className={embedded ? "space-y-6 pb-24" : "soft-card rounded-2xl p-6 space-y-6 pb-28 relative"} data-testid="visit-form">
      {!embedded && (
        <div className="flex items-center justify-between">
          <h3 className={`${isRTL ? "font-serif-ar" : "font-serif-en"} text-2xl text-[#2A2421]`}>
            {isRTL ? "زيارة جودة جديدة" : "New quality visit"}
          </h3>
          <button onClick={onClose} className="p-2 hover:bg-[#2A2421]/5 rounded-full" data-testid="visit-form-close">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="grid sm:grid-cols-3 gap-4">
        <div>
          <Label>{isRTL ? "الفرع" : "Branch"} *</Label>
          <Select value={branchId} onValueChange={setBranchId}>
            <SelectTrigger className="mt-1.5 h-11" data-testid="visit-branch-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {branches.map((b) => (
                <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>{isRTL ? "اسم المفتش" : "Inspector name"}</Label>
          <Input value={inspectorName} onChange={(e) => setInspectorName(e.target.value)} className="mt-1.5 h-11" data-testid="visit-inspector-input" />
        </div>
        <div>
          <Label>{isRTL ? "الباريستا المناوب" : "Barista on duty"}</Label>
          <Input value={baristaOnDuty} onChange={(e) => setBaristaOnDuty(e.target.value)} className="mt-1.5 h-11" data-testid="visit-barista-input" />
        </div>
      </div>

      <div className="space-y-3">
        {Object.entries(grouped).map(([groupKey, list]) => {
          const meta = GROUP_LABEL[groupKey] || { ar: groupKey, en: groupKey, emoji: "•" };
          const { evaluated, total } = groupProgress(list);
          const pct = total > 0 ? Math.round((evaluated / total) * 100) : 0;
          const isOpen = !!openGroups[groupKey];
          return (
            <div key={groupKey} className="rounded-xl border border-[#2A2421]/10 overflow-hidden" data-testid={`visit-group-${groupKey}`}>
              <button
                type="button"
                onClick={() => setOpenGroups((g) => ({ ...g, [groupKey]: !g[groupKey] }))}
                className="w-full flex items-center justify-between gap-3 p-4 bg-[#F9F6F0] hover:bg-[#F3EDDF] transition-colors"
                data-testid={`visit-group-toggle-${groupKey}`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-lg">{meta.emoji}</span>
                  <span className="text-sm font-semibold text-[#2A2421]">{meta[isRTL ? "ar" : "en"]}</span>
                  <span className="text-xs text-[#5C544F] bg-white rounded-full px-2 py-0.5">
                    {evaluated}/{total}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-24 h-1.5 bg-white rounded-full overflow-hidden">
                    <div className="h-full bg-[#C86240] transition-all" style={{ width: `${pct}%` }} />
                  </div>
                  {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </div>
              </button>
              {isOpen && (
                <div className="space-y-2 p-3 bg-white">
                  {list.map(({ it, meta: c }) => {
                    const label = it.label || c?.[isRTL ? "label_ar" : "label_en"] || it.key;
                    const target = c?.[isRTL ? "target_ar" : "target_en"];
                    const needsValue = c?.needs_value;
                    return (
                      <div key={it.key} className="grid sm:grid-cols-12 gap-2 items-center p-3 rounded-lg bg-[#F9F6F0]" data-testid={`visit-item-${it.key}`}>
                        <div className="sm:col-span-4">
                          <div className="text-sm text-[#2A2421] font-medium flex items-center gap-1.5">
                            {label}
                            {it._custom && (
                              <span className="text-[10px] uppercase tracking-wider bg-[#C86240]/15 text-[#C86240] rounded px-1.5 py-0.5">
                                {isRTL ? "مخصّص" : "Custom"}
                              </span>
                            )}
                          </div>
                          {target && (
                            <div className="text-xs text-[#5C544F]">
                              {isRTL ? "المرجع: " : "Target: "} {target}
                            </div>
                          )}
                        </div>
                        {needsValue && (
                          <Input
                            className="sm:col-span-2 h-9"
                            placeholder={isRTL ? "القيمة" : "Value"}
                            value={it.value || ""}
                            onChange={(e) => setItem(it.key, { value: e.target.value })}
                            data-testid={`visit-val-${it.key}`}
                          />
                        )}
                        <div className={`flex gap-1 ${needsValue ? "sm:col-span-3" : "sm:col-span-4"}`}>
                          {STATUS_BUTTONS.map(({ val, icon: Icon, color, border }) => (
                            <button
                              key={val}
                              type="button"
                              onClick={() => setItem(it.key, { status: val })}
                              className={`flex-1 h-9 inline-flex items-center justify-center gap-1 rounded-lg text-xs border transition-colors ${
                                it.status === val ? color : `bg-white ${border}`
                              }`}
                              data-testid={`visit-status-${it.key}-${val}`}
                              title={(isRTL ? STATUS_LABEL_AR : STATUS_LABEL_EN)[val]}
                            >
                              <Icon className="w-3.5 h-3.5" />
                            </button>
                          ))}
                        </div>
                        <Input
                          className={`${needsValue ? "sm:col-span-2" : "sm:col-span-3"} h-9`}
                          placeholder={isRTL ? "ملاحظة" : "Note"}
                          value={it.note || ""}
                          onChange={(e) => setItem(it.key, { note: e.target.value })}
                          data-testid={`visit-note-${it.key}`}
                        />
                        <button
                          type="button"
                          onClick={() => hideItem(it.key)}
                          className="sm:col-span-1 h-9 w-9 inline-flex items-center justify-center rounded-lg text-[#5C544F] hover:bg-red-50 hover:text-red-600 transition-colors"
                          title={isRTL ? "إزالة هذا البند من الزيارة" : "Remove this item"}
                          data-testid={`visit-remove-${it.key}`}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    );
                  })}
                  <AddCustomItemRow groupKey={groupKey} onAdd={addCustomItem} isRTL={isRTL} />
                </div>
              )}
            </div>
          );
        })}

        {hiddenCount > 0 && (
          <HiddenItemsPanel
            items={items.filter((it) => it._hidden)}
            catalog={catalog}
            onRestore={restoreItem}
            isRTL={isRTL}
          />
        )}
      </div>

      <div>
        <Label>{isRTL ? "ملاحظات عامة" : "Overall notes"}</Label>
        <Textarea value={overallNotes} onChange={(e) => setOverallNotes(e.target.value)} rows={3} className="mt-1.5" data-testid="visit-overall-notes" />
      </div>

      {/* Sticky save bar */}
      <div className="sticky bottom-0 left-0 right-0 -mx-6 -mb-6 px-6 py-3 bg-white/95 backdrop-blur border-t border-[#2A2421]/10 flex items-center justify-between gap-3 z-10" data-testid="visit-save-bar">
        <div className="flex items-center gap-2">
          <span className="text-xs text-[#5C544F]">{isRTL ? "النتيجة المتوقعة" : "Live score"}</span>
          <span className={`text-2xl font-bold ${previewColor}`} data-testid="visit-live-score">{livePreviewScore}</span>
          <span className="text-xs text-[#5C544F]">/100</span>
        </div>
        <div className="flex gap-2">
          <Button onClick={onClose} variant="outline" size="sm">{isRTL ? "إلغاء" : "Cancel"}</Button>
          <Button onClick={save} disabled={saving} className="bg-[#C86240] text-white" size="sm" data-testid="visit-save-btn">
            <FileText className="w-4 h-4 me-1.5" />
            {saving ? "..." : isEdit ? (isRTL ? "حفظ التعديلات" : "Save changes") : (isRTL ? "حفظ الزيارة" : "Save visit")}
          </Button>
        </div>
      </div>
    </Wrapper>
  );
}

// ============================================================
// Read-only visit detail modal
// ============================================================
function VisitDetailModal({ open, visit, checklist, lang, onClose, onEdit }) {
  const { isRTL } = useI18n();
  if (!visit) return null;

  const score = visit.overall_score || 0;
  const color = score >= 80 ? "text-emerald-600" : score >= 60 ? "text-amber-500" : "text-red-500";
  const dt = visit.visit_date ? new Date(visit.visit_date) : null;
  const pdfUrl = endpoints.myCafeVisitPdfUrl(visit.id, lang);
  const catalog = Object.fromEntries(checklist.map((c) => [c.key, c]));
  const grouped = {};
  (visit.items || []).forEach((it) => {
    const meta = catalog[it.key];
    if (!meta) return;
    (grouped[meta.group] = grouped[meta.group] || []).push({ it, meta });
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="visit-detail-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <span className={`text-3xl font-bold ${color}`}>{score}</span>
            <span>{visit.branch_name || (isRTL ? "(بدون فرع)" : "(no branch)")}</span>
          </DialogTitle>
          <DialogDescription>
            {dt ? dt.toLocaleString(isRTL ? "ar-SA" : "en-GB") : ""}
            {visit.barista_on_duty ? ` · ${visit.barista_on_duty}` : ""}
            {visit.inspector_name ? ` · ${isRTL ? "المفتش:" : "Inspector:"} ${visit.inspector_name}` : ""}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {Object.entries(grouped).map(([gkey, list]) => {
            const meta = GROUP_LABEL[gkey] || { ar: gkey, en: gkey, emoji: "" };
            return (
              <div key={gkey} className="rounded-xl border border-[#2A2421]/10 overflow-hidden">
                <div className="p-3 bg-[#F9F6F0] text-sm font-semibold text-[#2A2421]">
                  {meta.emoji} {meta[isRTL ? "ar" : "en"]}
                </div>
                <div className="divide-y divide-[#2A2421]/5">
                  {list.map(({ it, meta }) => {
                    const badge = STATUS_BUTTONS.find((s) => s.val === it.status);
                    return (
                      <div key={it.key} className="p-3 grid grid-cols-12 gap-3 text-sm items-center">
                        <div className="col-span-5">
                          <div className="text-[#2A2421]">{meta[isRTL ? "label_ar" : "label_en"]}</div>
                          {it.value && <div className="text-xs text-[#C86240]">{it.value}</div>}
                        </div>
                        <div className="col-span-3">
                          {badge && (
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${badge.color.split(" ")[0]} text-white`}>
                              <badge.icon className="w-3 h-3" />
                              {(isRTL ? STATUS_LABEL_AR : STATUS_LABEL_EN)[it.status]}
                            </span>
                          )}
                        </div>
                        <div className="col-span-4 text-xs text-[#5C544F] italic">{it.note || ""}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
          {visit.overall_notes && (
            <div className="rounded-xl bg-[#FFF7EE] border border-[#F1D7BC] p-4 text-sm">
              <b className="text-[#C86240]">{isRTL ? "ملاحظات عامة: " : "Overall notes: "}</b>
              {visit.overall_notes}
            </div>
          )}
        </div>

        <div className="flex gap-2 justify-end mt-4">
          <Button variant="outline" onClick={() => onEdit(visit)} data-testid="visit-detail-edit">
            <Pencil className="w-4 h-4 me-1.5" />
            {isRTL ? "تعديل" : "Edit"}
          </Button>
          <Button asChild className="bg-[#C86240] text-white" data-testid="visit-detail-pdf">
            <a href={pdfUrl} target="_blank" rel="noreferrer">
              <Printer className="w-4 h-4 me-1.5" />
              {isRTL ? "طباعة PDF" : "Print PDF"}
            </a>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


/* ─────────────────────────────────────────────────────────────────
   Helpers: Add custom item row + Hidden items restore panel
   ───────────────────────────────────────────────────────────────── */

function AddCustomItemRow({ groupKey, onAdd, isRTL }) {
  const [val, setVal] = useState("");
  const [open, setOpen] = useState(false);
  const submit = () => {
    if (!val.trim()) return;
    onAdd(groupKey, val);
    setVal("");
    setOpen(false);
  };
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="w-full mt-1 py-2.5 rounded-lg border border-dashed border-[#C86240]/40 text-[#C86240] text-sm font-medium hover:bg-[#C86240]/5 transition-colors inline-flex items-center justify-center gap-2"
        data-testid={`visit-add-custom-${groupKey}`}
      >
        <Plus className="w-4 h-4" />
        {isRTL ? "إضافة بند مخصّص" : "Add custom item"}
      </button>
    );
  }
  return (
    <div className="flex gap-2 items-center p-2 rounded-lg bg-[#FFF7EE] border border-[#F1D7BC]" data-testid={`visit-add-custom-form-${groupKey}`}>
      <Input
        autoFocus
        className="flex-1 h-9"
        placeholder={isRTL ? "اسم البند (مثل: درجة حرارة الفرن)" : "Item name (e.g. Oven temperature)"}
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); submit(); }
          if (e.key === "Escape") { setOpen(false); setVal(""); }
        }}
        data-testid={`visit-add-custom-input-${groupKey}`}
      />
      <Button type="button" size="sm" onClick={submit} className="bg-[#C86240] text-white h-9" data-testid={`visit-add-custom-save-${groupKey}`}>
        <Check className="w-4 h-4" />
      </Button>
      <Button type="button" size="sm" variant="outline" onClick={() => { setOpen(false); setVal(""); }} className="h-9">
        <X className="w-4 h-4" />
      </Button>
    </div>
  );
}

function HiddenItemsPanel({ items, catalog, onRestore, isRTL }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border border-dashed border-[#2A2421]/15 bg-[#F9F6F0]/60" data-testid="visit-hidden-panel">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3 p-3 text-sm text-[#5C544F] hover:text-[#2A2421] transition-colors"
        data-testid="visit-hidden-toggle"
      >
        <span className="inline-flex items-center gap-2 font-medium">
          <Eye className="w-4 h-4 opacity-60" />
          {isRTL ? `بنود مخفية (${items.length})` : `Hidden items (${items.length})`}
        </span>
        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      {open && (
        <div className="px-3 pb-3 flex flex-wrap gap-2">
          {items.map((it) => {
            const c = catalog[it.key];
            const label = it.label || c?.[isRTL ? "label_ar" : "label_en"] || it.key;
            return (
              <button
                key={it.key}
                type="button"
                onClick={() => onRestore(it.key)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#2A2421]/10 text-xs text-[#2A2421] hover:border-[#C86240] hover:text-[#C86240] transition-colors"
                data-testid={`visit-restore-${it.key}`}
                title={isRTL ? "استعادة" : "Restore"}
              >
                <RotateCcw className="w-3 h-3" />
                <span>{label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
